import React from 'react';
import styled from 'styled-components';
import { Select } from '../../../../common/Select';
import { ContactGender, CONTACT_GENDER_LABELS } from '../../../../../types/contact';
import { ExtendedMarkerData } from '../../../../../types/marker';
import { v4 as uuidv4 } from 'uuid';

interface ContactGenderSelectProps {
  formData: ExtendedMarkerData;
  setFormData: React.Dispatch<React.SetStateAction<ExtendedMarkerData>>;
  error?: string;
  isReadOnly?: boolean;
  contactIndex?: number; // Index du contact actif
}

const GenderField = styled.div`
  position: relative;
  margin-bottom: 3px;
  flex: 0 0 60px;  /* Largeur fixe réduite pour la civilité */
  min-width: 60px;
  max-width: 60px;
  
  /* Ajuster la taille du select pour correspondre aux champs textes */
  select {
    font-size: 0.75rem;
    padding: 2px 6px;
    padding-right: 24px; /* Espace pour la flèche */
    height: 26px;
    width: 100%;
    font-weight: 400;
    border: 1px solid ${({ theme }) => theme.colors.border};
    border-radius: 4px;
    
    /* Réduire l'espace autour du texte dans les options */
    option {
      padding: 2px 4px;
    }
  }
  
  label {
    margin-bottom: 2px;
    font-size: 0.65rem;
    color: ${({ theme }) => theme.colors.textLight};
  }
  
  /* Responsive: sur mobile */
  @media (max-width: 768px) {
    margin-bottom: 3px;
    flex: 0 0 60px;
    min-width: 60px;
    max-width: 60px;
    
    select {
      font-size: 0.75rem;
      height: 26px;
      border-radius: 4px;
      padding: 2px 6px;
      padding-right: 24px;
    }
    
    label {
      margin-bottom: 2px;
      font-size: 0.65rem;
    }
  }
  
  /* Très petit écran */
  @media (max-width: 480px) {
    flex: 1;  /* Prend toute la largeur sur très petit écran */
    min-width: auto;
    max-width: none;
  }
`;

/**
 * Options pour le select de genre/civilité
 */
const GENDER_OPTIONS = Object.entries(CONTACT_GENDER_LABELS).map(([value, label]) => ({
  value,
  label
}));

/**
 * Composant pour sélectionner la civilité d'un contact (Mr, Me, Sté, SCI)
 * Gère automatiquement la synchronisation entre formData et contacts[0]
 */
export const ContactGenderSelect: React.FC<ContactGenderSelectProps> = ({
  formData,
  setFormData,
  error,
  isReadOnly = false,
  contactIndex = 0
}) => {
  // Récupérer le genre actuel depuis le contact actif
  const currentGender = formData.contacts?.[contactIndex]?.gender || '';

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newGender = e.target.value as ContactGender;
    const updatedContacts = [...(formData.contacts || [])];
    
    // Créer ou mettre à jour le contact actif
    if (updatedContacts.length === 0 || !updatedContacts[contactIndex]) {
      // Créer un nouveau contact avec toutes les données existantes
      const newContact = {
        id: uuidv4(),
        gender: newGender,
        name: formData.name || '',
        firstName: formData.firstName || '',
        type: 'owner' as const,
        address: formData.contact_address || '',
        phone: formData.phone || '',
        email: formData.email || '',
        designation: 'contact' as const
      };
      updatedContacts.push(newContact);
    } else {
      // Mettre à jour le contact actif
      updatedContacts[contactIndex] = {
        ...updatedContacts[contactIndex],
        gender: newGender
      };
    }
    
    // Mettre à jour formData avec le nouveau genre et les contacts synchronisés
    setFormData((prev: ExtendedMarkerData) => ({
      ...prev,
      contacts: updatedContacts
    }));
  };

  return (
    <GenderField>
      <Select
        options={GENDER_OPTIONS}
        value={currentGender}
        onChange={handleChange}
        disabled={isReadOnly}
        error={error}
        placeholder="Civilité"
      />
    </GenderField>
  );
};
